import type { Handler } from './handler';
import type { ApiFetchPlayerProfilePort } from './ports/api-fetch-player-profile.port';
import {
  PlayerProfileError,
  type PlayerProfileErrorKind,
} from '~/adapters/api-player-profile/player-profile-api';
import type { PlayerProfile } from '~/types/player-profile';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { normalizePlayerVip } from '~/utils/apostatudo/player-vip';
import { resolveAdapter } from '~/adapters/registry';

// Cache do último "sim" desta aba. É CACHE DE LEITURA, e não memo: ele nunca
// corta a requisição — o core é sempre perguntado e a resposta dele manda.
//
// Existe só pra cobrir a janela entre o F5 e a resposta chegar. Sem ele a store
// sobe sem nível VIP e o card da roletinha (Clube Fidelidade) nasce na arte
// padrão e troca sozinho quando o /players/me responde; guardando a última
// resposta, o card já nasce certo.
//
// Guarda a resposta inteira (não só o booleano) exatamente por causa do `vip`:
// era um cache só de flag que fazia o tier desaparecer no primeiro F5.
//
// sessionStorage, e não localStorage: por aba, some ao fechar. Não fica grudado
// no navegador do jogador, e como o core é reconsultado sempre, um valor errado
// aqui se conserta sozinho na primeira resposta.
const PROFILE_KEY_PREFIX = 'apt:player-profile:';

function profileKey(userId: number): string {
  return `${PROFILE_KEY_PREFIX}${userId}`;
}

// Última resposta conhecida desta aba, pra quem quiser exibir alguma coisa
// enquanto o core não responde. Quem chama é a store, no começo do `ensure()` —
// o handler NÃO consulta isto: a requisição sai de qualquer jeito.
//
// SSR não tem sessionStorage, e navegador em modo restrito pode lançar no
// acesso — nos dois casos simplesmente não há cache e a tela espera a resposta.
//
// O `vip` é revalidado como se viesse da rede (mesmo util do mapper): storage é
// editável pelo devtools e é a `tag` que escolhe a arte do card. `isEligible` nem
// é lido de lá — a EXISTÊNCIA da entrada já é o "sim", porque só gravamos nesse
// caso.
export function readPlayerProfileCache(userId: number): PlayerProfile | null {
  if (!import.meta.client) return null;

  try {
    const raw = sessionStorage.getItem(profileKey(userId));

    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') return null;

    return {
      userId,
      isEligible: true,
      vip: normalizePlayerVip((parsed as { vip?: unknown }).vip),
      // O slug do CRM NÃO hidrata do cache de propósito: ele só serve pra regra
      // de divergência do sync-level, que não pode disparar em cima de um valor
      // velho de sessionStorage — só a resposta fresca do core conta.
      level: null,
    };
  } catch {
    return null;
  }
}

// Só o `vip` vai pro storage: o `userId` está na chave e o `isEligible` é
// implícito (a entrada só existe pra quem o core afirmou elegível).
function writeCache(profile: PlayerProfile): void {
  if (!import.meta.client) return;

  try {
    sessionStorage.setItem(
      profileKey(profile.userId),
      JSON.stringify({ vip: profile.vip }),
    );
  } catch {
    // Sem cache o fluxo segue: só não há o que mostrar antes da resposta.
  }
}

function removeCache(userId: number): void {
  if (!import.meta.client) return;

  try {
    sessionStorage.removeItem(profileKey(userId));
  } catch {
    // Nada a fazer: sem storage não havia cache pra limpar.
  }
}

// A resposta NÃO é memoizada por sessão: quem chama quer o estado do momento.
// Já existiram aqui dois cortes maiores — um freio de 60s pro "não" e o
// curto-circuito do "sim" pelo storage — removidos de propósito, porque os dois
// congelavam o nível VIP, que vem na mesma resposta e muda (o core o resolve por
// join a cada leitura; só a elegibilidade ele memoiza).
//
// O que sobra são duas defesas contra pergunta REPETIDA, não contra pergunta:
//
// 1. Requisição em vôo compartilhada, pra duas chamadas simultâneas serem uma.
// 2. A janela de dedupe abaixo, pra duas chamadas em sequência curta serem uma.
let inflight: { userId: number; promise: Promise<PlayerProfile> } | null = null;

// Janela em que a última resposta é servida de novo sem ir à rede.
//
// Os gatilhos se sobrepõem por construção: voltar o foco pra aba e abrir o Clube
// Fidelidade em seguida são dois `ensure()` a segundos de distância, e o segundo
// não tem como trazer novidade — nem a elegibilidade (monotônica, e o core ainda
// a memoiza por 1 min) nem o tier, que não muda em 10 segundos.
//
// 10s, e não os 60s do freio antigo, porque o alvo aqui é outro: aquele calava a
// pergunta por um minuto inteiro (e com ela o tier); este só absorve a duplicata
// imediata. Fica em memória — um F5 volta a perguntar.
const DEDUPE_WINDOW_MS = 10 * 1000;

let lastAnswer: { userId: number; at: number; profile: PlayerProfile } | null =
  null;

export const fetchPlayerProfileHandler: Handler<Params, Ports, Result> = async (
  params,
  ports = {
    apiFetchPlayerProfile: resolveAdapter('apiFetchPlayerProfile'),
  },
) => {
  const { userId } = params;

  // Duplicata imediata: serve a última resposta sem tocar a rede.
  if (
    lastAnswer?.userId === userId &&
    Date.now() - lastAnswer.at < DEDUPE_WINDOW_MS
  ) {
    return { profile: lastAnswer.profile, failure: null };
  }

  let promise = inflight?.userId === userId ? inflight.promise : undefined;

  if (!promise) {
    promise = ports.apiFetchPlayerProfile.handle({ userId });
    inflight = { userId, promise };
  }

  const [profile, error] = await tryCatch(promise);

  if (inflight?.promise === promise) inflight = null;

  if (error || !profile) {
    console.error('[fetchPlayerProfileHandler] falha ao buscar:', error);

    // Falha não mexe no cache: o que estava lá continua sendo a última resposta
    // conhecida, que é melhor que nada pro próximo F5. Também não abre janela de
    // dedupe — a próxima pergunta tem que ir à rede, senão uma queda de rede
    // silenciaria as tentativas seguintes.
    //
    // O MOTIVO sobe pra store, porque desde a introspecção do token no core as
    // falhas deixaram de ser equivalentes: 401 é veredito sobre a sessão, 503 é
    // indisponibilidade transitória — e cada uma pede uma reação diferente.
    return {
      profile: null,
      failure: error instanceof PlayerProfileError ? error.kind : 'api',
    };
  }

  // O cache espelha a última resposta, inclusive quando ela é "não" — senão o
  // F5 seguinte hidrataria a tela com um "sim" que o core já desmentiu.
  if (profile.isEligible) {
    writeCache(profile);
  } else {
    removeCache(userId);
  }

  lastAnswer = { userId, at: Date.now(), profile };

  return { profile, failure: null };
};

// Zera o que se sabe deste jogador. Quem chama é o plugin de sessão
// (plugins/player-profile.client.ts), no logout e na troca de conta: o que se
// sabia era daquele jogador.
export function clearPlayerProfileCache(userId?: number): void {
  inflight = null;
  // A janela de dedupe também morre aqui: a resposta guardada é do jogador que
  // saiu, e servi-la pra quem entrar depois seria dar o veredito do outro.
  lastAnswer = null;

  if (userId === undefined) return;

  removeCache(userId);
}

type Params = { userId: number };

type Ports = { apiFetchPlayerProfile: ApiFetchPlayerProfilePort };

// `profile: null` = nada novo a dizer (a busca falhou); `failure` diz POR QUÊ.
// Quem chama preserva o que já tinha em vez de rebaixar o jogador por silêncio,
// e decide pela causa se há algo mais a fazer (ver stores/player-profile.ts).
export type PlayerProfileFetch =
  | { profile: PlayerProfile; failure: null }
  | { profile: null; failure: PlayerProfileErrorKind };

type Result = Promise<PlayerProfileFetch>;
